import { byAttrVal, createCustomElement, DOMModel, registerEvent } from '@adobe/react-webcomponent'
import { Attachment, ModalContextProvider } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { QueryClientProvider } from 'react-query'
import { MemoryRouter } from 'react-router'

import { ChooseAttachmentEmberModal } from '@features/attachments/elements/ChooseAttachmentEmberModal'
import {
  UserOrganizationContextProvider,
  UserOrganizationSettingsContextProvider,
} from '@modules-deprecated/app/organization'

import { queryClient } from '../../config/queryClient'
import { EmberCloseModalContextProvider } from '../../contexts/emberCloseModalContext'
import { EmberNavigate, EmberRouterContextProvider } from '../../contexts/emberRouterContext'
import { ThemeProvider } from '../../contexts/themeProvider'
import { ModalId } from '../../enums/modalId'
import { EmberEventFn } from '../../types/emberEventFn'

class ChooseReceiptModalModuleWebComponent extends DOMModel {
  @byAttrVal organizationId: string
  @byAttrVal idsToSkipStringified: string
  @registerEvent('closeEmber') closeEmber: Function
  @registerEvent('receiptSelect') receiptSelect: Function
  @registerEvent('navigate') navigate: Function | undefined
}

type ChooseReceiptModalProps = {
  idsToSkipStringified?: string
  onCloseEmber: EmberEventFn
  onNavigate: EmberNavigate
  onReceiptSelect: EmberEventFn
  organizationId?: string
}

export const ChooseReceiptModalModule = ({
  idsToSkipStringified,
  onCloseEmber,
  onNavigate,
  onReceiptSelect,
  organizationId,
}: ChooseReceiptModalProps): ReactElement => {
  const idsToSkip = idsToSkipStringified ? JSON.parse(idsToSkipStringified) : []

  const handleReceiptSelect = useCallback(
    (attachment: Attachment) => {
      onReceiptSelect({ detail: { attachment } })
    },
    [onReceiptSelect],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <MemoryRouter>
          <EmberCloseModalContextProvider onCloseModal={onCloseEmber}>
            <EmberRouterContextProvider onNavigate={onNavigate}>
              <UserOrganizationContextProvider organizationId={organizationId}>
                <UserOrganizationSettingsContextProvider organizationId={organizationId}>
                  <ModalContextProvider defaultOpenedIds={[ModalId.AttachmentsModal]}>
                    <ChooseAttachmentEmberModal
                      onSelect={handleReceiptSelect}
                      id={ModalId.AttachmentsModal}
                      idsToSkip={Array.isArray(idsToSkip) ? idsToSkip : []}
                    />
                  </ModalContextProvider>
                </UserOrganizationSettingsContextProvider>
              </UserOrganizationContextProvider>
            </EmberRouterContextProvider>
          </EmberCloseModalContextProvider>
        </MemoryRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

const ChooseReceiptModalCustomElement = createCustomElement(
  ChooseReceiptModalModule,
  ChooseReceiptModalModuleWebComponent,
  'element',
)

customElements.get('choose-receipt-modal-module') ||
  customElements.define('choose-receipt-modal-module', ChooseReceiptModalCustomElement)

export default ChooseReceiptModalCustomElement
